import React, { useRef, useEffect, useCallback, useState} from 'react';
import {useContextMenu} from "components/dropdowns/useContextMenu.js";
import TrackContextMenu from "components/v3/sidebar/track/TrackContextMenu.js";
import SliderMaterial from './SliderMaterial.js';
import {isMobile} from 'react-device-detect';
import {PIANO_ROLL} from 'constants/editor-bottom-modes.js';
import TrackMeter from "components/TrackMeter.js";
import PadStyle from './PadStyle.js';
import {useDoubleClick} from "components/hooks/useDoubleClick.js"
import {
    PADS_SLOT_EMPTY,
    PADS_SLOT
} from "constants/contextual-help.js";
import ContextMenu from "components/dropdowns/ContextMenu.js";
import MenuItem from "components/dropdowns/MenuItem.js";
import {useSlotDrop} from './useSlotDrop.js';
import Colors from 'components/Colors.js';
import {useVolumeMove} from "components/hooks/useVolumeMove.js";
import TrackThumbnail from "components/TrackThumbnail.js";
import {connect} from 'react-redux';
import {
    setArrangementBottomMode,
    setSelectedZObject,
    setShowPianoRoll,
    setContextualHelp,
    setParameterSliderDown,
    setSampleTagDragging,
    setTrackDragging,
    setSampleDragging,
    setShowEditorBottom
} from 'actions/index.js';
import {DELETE,MUTES} from './modes.js';

const colors = new Colors();
const DOUBLE_CLICK = 300;

const PadSlot = ({
    muted,
    showCanvas,
    attributes,
    setSelectedZObject,
    size,
    setShowEditorBottom,
    trackToShow,
    scale,
    setContextualHelp,
    sampleId,
    setTrackToShow,
    playing,
    setParameterSliderDown,
    notesTriggered,
    slotNumber,
    slot,
    pad,
    sampleDragging,
    setSampleDragging,
    isSelected,
    sampleTagDragging,
    setSampleTagDragging,
    mode,
    setArrangementBottomMode,
    setShowPianoRoll,
    isEnabled,
    volume,
    setTrackDragging,
    trackDragging,
    sliderMaterial
}) => { 
    const ref = useRef(undefined);
    const innerRef = useRef(undefined);

    const {onContextMenu, dropdownPos, setDropdownPos} = useContextMenu({ref, useTop: true});

    const [draggable, setDraggable] = useState(false);

    const onDoubleClick = useCallback((e) => {
        e.stopPropagation();
        if (slot) {
            // then show the bottom bar piano roll
            setShowEditorBottom(pad.state.spatial.showEditorBottom === PIANO_ROLL ? undefined : PIANO_ROLL);
        }
    }, [slot]);

    const onClick = useDoubleClick(onDoubleClick, 250, e => e.stopPropagation());

    useEffect(() => {
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
        };
    }, [setDraggable]);

    useEffect(() => {
        setDraggable(false);
    }, [trackDragging, slot, setDraggable]);

    const onKeyDown = useCallback((e) => {
        if (e.key === "Shift") {
            setDraggable(true);
        }
    }, [setDraggable]);

    const onKeyUp= useCallback((e) => {
        if (e.key === "Shift") {
            setDraggable(false);
        }
    }, [setDraggable]);

    const {
        innerStyle,
        onMouseDown,
        onMouseMove,
        onMouseLeave,
        mouseDown,
        onMouseUp} = useVolumeMove(ref, slot, setParameterSliderDown, mode, volume, [ref, innerRef], pad);

    const {onDrop} = useSlotDrop({
        sampleDragging,
        slotNumber,
        pad,
        sampleTagDragging,
        setSampleDragging,
        setSampleTagDragging,
        slot,
        trackDragging,
        setTrackDragging
    });

    const _onMouseDown = useCallback((e) => {
        e.stopPropagation();
        if (slot) {
            pad.sequencerEngine.selectTrack(
                slot.trackNumber);
            onMouseDown(e);

            if (mode === DELETE) {
                slot.deleting = true;
            } else if (mode === MUTES) {
                slot.mute(!muted);
            }
        }
    }, [draggable, mode, slot, onMouseDown, trackToShow, setTrackToShow, muted]);

    const _onMouseUp = useCallback((e) => {
        e.stopPropagation();
        if (slot) {
            pad.sequencerEngine.selectTrack(
                slot.trackNumber);
            onMouseUp(e);
            if (mode === DELETE) {
                slot.deleting = false;
            }
        }
    }, [mode, slot, onMouseUp]);

    const selectTrack = useCallback((e) => {
        e.stopPropagation();
        if (slot) {
            pad.sequencerEngine.selectTrack(
                slot.trackNumber);
        }
        if (pad.sequencerEngine.state.zobject.selectedZObject) {
            setSelectedZObject(undefined);
        }
    }, [slot, pad]);

    const toggleGated = useCallback((e) => {
        if (slot) {
            slot.gated = !slot.gated;
        }
    }, [slot]);

    const toggleRecordingEnabled = useCallback((e) => {
        e.stopPropagation();
        slot.toggleEnabled();
    }, [isEnabled, slot]);

    let className = "pad-slot";
    if (!slot) {
        className += " empty";
    }
    if (isSelected) {
        className += " selected";
    }

    if (mode === DELETE && mouseDown) {
        className += " deleting";
    }

    if (muted) {
        className += " muted";
    }

    let keyMapping = undefined;
    if (slot) {
        keyMapping = slot.getKeyMapping();
    }

    if (draggable) {
        className += " slot-draggable";
    }

    let color = !showCanvas && slot && colors.getColorForTrack(slot);
    if (color && slot.sample && (
        slot.sample.getThumbnail() &&
            slot.sample.getThumbnail() !== '')) {
    } else if (color) {
    }

    if (attributes && attributes["slot-color"]) {
        color = colors.transparent(
            colors.saturate(attributes["slot-color"], 1),
            attributes["slot-opacity"] || 1);
    }

    let style = {
        backgroundColor: color,
        width: size,
        height: size,
        margin: 3*scale + 'px',
    };

    // className += " pushable";

    useEffect(() => {
        if (isMobile) {
            ref.current.addEventListener("touchmove", onMouseMove, {passive: false});
        }

        return () => {
            if (ref.current && isMobile) {
                ref.current.removeEventListener("touchmove", onMouseMove, {passive: false});
            }
        };
    }, [slot, mode]);


    let hideNames = attributes && !attributes["Show Track Names"];

    return (
        <div
          style={style}
          onContextMenu={onContextMenu}
          ref={ref}
          onClick={onClick}
          onMouseMove={onMouseMove}
          onMouseOver={() => setContextualHelp(
              slot ? PADS_SLOT : PADS_SLOT_EMPTY)}
          onTouchStart={onMouseDown}
          onMouseUp={_onMouseUp}
          onMouseLeave={(e) => {
              setContextualHelp(undefined);
              onMouseLeave(e);
          }}
          onDragEnter={(e) => {
              e.dataTransfer.dropEffect = "move";
          }}
          onDragOver={(e) => {
              e.dataTransfer.dropEffect = "move";
              e.stopPropagation();
              e.preventDefault();
          }}
          onDrop={onDrop}
          onMouseDown={slot ? _onMouseDown : () => []}
          className={className}>
          {slot && <TrackContextMenu
            dropdownPos={dropdownPos}
            setDropdownPos={setDropdownPos}
                     track={slot}/>}
 
          <PadStyle padRef={ref} track={slot}/>
          <div className="inner-slot">
          {slot && !hideNames ? <TrackThumbnail
                                  isPad={true}
                    scale={scale}
                    onMouseDown={(e) => {
                        if (!e.metaKey && !muted) {
                            e.stopPropagation();
                        }
                    }}
                                  showPresetName={true}
                                  track={slot}/> : ''}
            <div
              ref={innerRef}
              className={mouseDown ? "volume-indicator"  : "volume-indicator skinny"} style={{
                ... innerStyle,
                width: mouseDown ? size : undefined
              }}>
              {sliderMaterial && <SliderMaterial
                                   pad={pad}
                                   mouseDown={mouseDown}
                                   slotNumber={slotNumber}
                                   sliderMaterial={sliderMaterial}
                                   style={{... innerStyle, width: mouseDown ? size : 8}}/>}
            </div>
            {!showCanvas && slot && <TrackMeter height={size} meter={slot.trackMeter}/>}
          {slot &&
           <div
             onMouseDown={toggleRecordingEnabled}
             className={isEnabled ? "recording-dot enabled" : "recording-dot"}
           /> }
          {keyMapping && 
           <div className="pad-key-map">{keyMapping.toUpperCase()}</div>}
          </div>
        </div>
    );
}

const mapStateToProps = (state, props) => {
    let track = props.slot;
    //if (track && track.tracks && track.tracks[0]) {
    //    track = track.tracks[0].track;
    //}
    let ret = {
        sliderMaterial: props.pad.sliderMaterial,
        sampleId: !track  ? 0 :
            track.sample !== undefined && track.sample.getId() !== undefined ?
            track.sample.getId() : track.presetName || track.getName(),
        sampleDragging: state.selector.sampleDragging,
        sampleTagDragging: state.selector.sampleTagDragging,
        isSelected: props.slot && state.mix.selectedTrack === props.slot.trackNumber,
        trackDragging: state.tracks.trackDragging,
    };
    let slot = props.slot;
    /*
    if (slot) {
        let trackTriggers = state.tracks.triggers[slot.trackNumber];
        ret.playing = (trackTriggers && Object.keys(trackTriggers).some(
            t => trackTriggers[t])) ||
            (props.slot.notesTriggered && Object.keys(props.slot.notesTriggered).length > 0);
        ret.notesTriggered = props.slot.notesTriggered;
    }
    */
    if (slot) {
        ret.isEnabled = slot.isEnabled;
        ret.muted = state.tracks.trackMutes[slot.id]; 
        ret.volume = state.tracks.trackVolumes[slot.id];
    }
    return ret;
};

const mapDispatchToProps = (dispatch) => {
  return {
      setSampleDragging : (sample) => dispatch(setSampleDragging(sample)),
      setTrackDragging : (track) => dispatch(setTrackDragging(track)),
      setSampleTagDragging : (sample) => dispatch(setSampleTagDragging(sample)),
      setParameterSliderDown: parameter => dispatch(setParameterSliderDown(parameter)),
      setArrangementBottomMode: x => dispatch(setArrangementBottomMode(x)),
      setShowPianoRoll: x => dispatch(setShowPianoRoll(x)),
      setContextualHelp: x => dispatch(setContextualHelp(x)),
      setSelectedZObject: x => dispatch(setSelectedZObject(x)),
      setShowEditorBottom: x => dispatch(setShowEditorBottom(x))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PadSlot);
